import type { ActorRecord,
  Project,
  ProjectListItem,
  PaginatedResponse,
  CreateProjectResponse,
  CreateProjectRequest,
  ProjectStatus,
  ProjectConfig,
  UpdateProjectConfigRequest,
  DashboardTab,
  TabSortBy,
  TabSortDirection,
} from './types'
import { getDefaultApiBase } from '../utils/environment'
import { createDebug } from '../utils/debugLog'

// Transient-failure retry interceptor — see withTransientRetry below.
// Debug logger gated by VITE_NAD_DEBUG / `localStorage.debug = 'dw:retry:*'`.
const debugTransientRetry = createDebug('dw:retry:transient')

type ApiValue = string | number | boolean | null | object | undefined
type ApiRecord = Record<string, ApiValue>

function parseJsonResponse<T>(text: string): T {
  return JSON.parse(text)
}

function isApiRecord(value: ApiValue | null): value is ApiRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringField(record: ApiRecord, key: string): string | undefined {
  const value = record[key]
  return typeof value === 'string' ? value : undefined
}

// Storage keys
const API_BASE_KEY = 'ponpon_api_base'
const GOOGLE_API_KEY = 'ponpon_google_api_key'
const VEO3_API_KEYS_KEY = 'ponpon_veo3_api_keys'
const MUSICGPT_API_KEY_KEY = 'ponpon_musicgpt_api_key'
const VIDEO_SIMULATOR_MODE_KEY = 'ponpon_video_simulator_mode'

// Auth token getter - set by AuthContext to provide Firebase ID token
let authTokenGetter: (() => Promise<string | null>) | null = null
// Cached token for sync access (used for asset URLs in <img> tags)
let cachedAuthToken: string | null = null

// Publication token for unauthenticated access to published project assets.
// Set when viewing a published project page; used as fallback when asset URLs
// in the snapshot weren't rewritten to signed storage URLs.
let currentPublicationToken: string | null = null

export function setPublicationToken(token: string | null): void {
  currentPublicationToken = token
}

export function getPublicationToken(): string | null {
  return currentPublicationToken
}

export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  authTokenGetter = getter
  // Update cached token immediately
  getter().then(token => {
    cachedAuthToken = token
  })
}

export function setCachedAuthToken(token: string | null): void {
  cachedAuthToken = token
}

// account-self-service: Optional global hook invoked on any 4xx response so
// AuthContext can detect ``{"detail": {"restore_required": true}}`` payloads
// and route the user to /account/restore.  Set to null to disable.
type UnauthorizedHandler = (res: Response) => void | Promise<void>
let unauthorizedHandler: UnauthorizedHandler | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler
}

async function _maybeNotifyUnauthorized(res: Response): Promise<void> {
  if (!unauthorizedHandler) return
  if (res.status !== 401 && res.status !== 403) return
  try {
    await unauthorizedHandler(res)
  } catch {
    /* never let the handler break the response chain */
  }
}

export async function getAuthToken(): Promise<string | null> {
  if (authTokenGetter) {
    const token = await authTokenGetter()
    cachedAuthToken = token // Keep cache in sync
    return token
  }
  return null
}

// =============================================================================
// Centralized fetch (read this before adding new HTTP calls)
// =============================================================================
// All HTTP calls from the Console MUST go through `apiFetch` (raw Response) or
// `apiRequest` (parsed JSON). They set:
//   - credentials: 'include'   (session affinity / cookies)
//   - Authorization             (Firebase ID token, when available)
//   - Content-Type: application/json (skipped for FormData bodies)
//   - Optional X-Google-API-Key / X-VEO3-API-Keys / X-MusicGPT-API-Key
//
// Calling `fetch()` directly will silently drop these defaults — including
// session affinity — so don't. The only legitimate raw `fetch()` users are:
//   - same-origin /dev/api/* dashboard endpoints
//   - asset URL downloads (signed storage URLs, blob fetches)

export interface ApiFetchOptions extends RequestInit {
  /** Adds X-Google-API-Key for generation requests */
  includeApiKey?: boolean
  /** Adds X-Google-API-Key + X-VEO3-API-Keys + X-MusicGPT-API-Key for trailer generation */
  includeTrailerKeys?: boolean
  /** Skip the Authorization header (use for unauthenticated endpoints like /auth/signup) */
  skipAuth?: boolean
}

async function buildRequestInit(opts: ApiFetchOptions = {}): Promise<RequestInit> {
  const { includeApiKey, includeTrailerKeys, skipAuth, ...init } = opts
  const headers = new Headers(init.headers)

  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (!skipAuth) {
    const token = await getAuthToken()
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }
  }

  if (includeApiKey) {
    const key = getGoogleApiKey()
    if (key && !headers.has('X-Google-API-Key')) {
      headers.set('X-Google-API-Key', key)
    }
  }
  if (includeTrailerKeys) {
    const g = getGoogleApiKey()
    if (g && !headers.has('X-Google-API-Key')) headers.set('X-Google-API-Key', g)
    const v = getVeo3ApiKeys()
    if (v && !headers.has('X-VEO3-API-Keys')) headers.set('X-VEO3-API-Keys', v)
    const m = getMusicGptApiKey()
    if (m && !headers.has('X-MusicGPT-API-Key')) headers.set('X-MusicGPT-API-Key', m)
  }

  return {
    ...init,
    credentials: init.credentials ?? 'include',
    headers,
  }
}

/**
 * Generic transient-failure retry policy. Delays in milliseconds before
 * each successive retry attempt. 3 retries total ≈ 7s of backoff.
 *
 * Triggered by:
 *  - Network errors (fetch threw — connection refused, timeout, DNS)
 *  - Any 5xx HTTP response (server-side transient error, including 502
 *    when an instance was killed mid-request, 503 service unavailable,
 *    504 gateway timeout)
 *
 * NOT retried:
 *  - 4xx responses (client error — retry won't fix it)
 *  - Final attempt's failure (returned to caller)
 *
 * Note on idempotency: this currently retries ALL 5xx including for
 * non-idempotent POSTs. A request that the server processed but whose
 * response was lost (mid-response instance kill) will be retried,
 * potentially producing a duplicate (e.g. duplicate Director response
 * or duplicate node creation). Endpoints with explicit idempotency keys
 * (sparks deduct, queued generation claim_ydoc_apply) handle this
 * safely. Per-call idempotency tokens for other endpoints are tracked
 * as separate work.
 */
const TRANSIENT_RETRY_DELAYS_MS: number[] = [1000, 2000, 4000]

class ServiceUnavailableError extends Error {
  constructor(
    public readonly code: string | undefined,
    public readonly retryAfterMs: number | undefined,
    public readonly response: Response,
  ) {
    super(`Service unavailable (${code ?? 'unknown'})`)
    this.name = 'ServiceUnavailableError'
  }
}

function _sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Wrap *fn* with retry on transient failures (network errors + 5xx).
 *
 * Returns:
 *  - The first 2xx/3xx/4xx response (no retry)
 *  - The final response if all retries exhausted (caller decides what
 *    to do — apiRequest raises a typed error from the body)
 *
 * Throws:
 *  - The fetch error from the final attempt if every retry threw
 */
async function withTransientRetry(
  url: string,
  fn: () => Promise<Response>,
): Promise<Response> {
  let attempt = 0
  let lastError: Error | undefined
  while (attempt <= TRANSIENT_RETRY_DELAYS_MS.length) {
    try {
      const res = await fn()
      // Retry on any 5xx — covers 502 (backend killed mid-request),
      // 503 (no healthy backends / draining), 504 (gateway timeout).
      if (res.status >= 500 && res.status < 600) {
        if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
          const delay = TRANSIENT_RETRY_DELAYS_MS[attempt]
          debugTransientRetry({ event: 'retry', status: res.status, attempt: attempt + 1, delay_ms: delay, url })
          await _sleep(delay)
          attempt++
          continue
        }
        debugTransientRetry({ event: 'exhausted', status: res.status, url })
        return res
      }
      return res
    } catch (err) {
      // Network error (fetch threw): connection refused, timeout, DNS.
      lastError = err instanceof Error ? err : new Error(String(err))
      if (attempt >= TRANSIENT_RETRY_DELAYS_MS.length) {
        debugTransientRetry({ event: 'exhausted-network', url })
        throw err
      }
      const delay = TRANSIENT_RETRY_DELAYS_MS[attempt]
      debugTransientRetry({ event: 'retry-network', attempt: attempt + 1, delay_ms: delay, url })
      await _sleep(delay)
      attempt++
    }
  }
  // Defensive — loop exit without explicit return/throw; rethrow last error
  // if any, otherwise this is unreachable.
  throw lastError
}

/**
 * Issue an HTTP call to the backend with all shared headers + credentials applied.
 * Returns the raw `Response` — use this for SSE / streaming / non-JSON downloads.
 *
 * `path` may be a relative path (prepended with the configured API base) or an
 * absolute URL (used as-is — useful for already-built URLs).
 *
 * Every call is wrapped in `withTransientRetry` so transient failures
 * (network errors and 5xx responses) are retried with exponential backoff.
 * Per the simplify-failover RFC, this replaces the per-error-code retry
 * policy that depended on the now-deleted node_draining /
 * project_owned_elsewhere 503 codes.
 */
export async function apiFetch(path: string, opts?: ApiFetchOptions): Promise<Response> {
  const init = await buildRequestInit(opts)
  const url = path.startsWith('http://') || path.startsWith('https://')
    ? path
    : `${getApiBase()}${path}`
  const response = await withTransientRetry(url, () => fetch(url, init))
  // Notify the global handler (account-self-service) — fire-and-forget.
  if (response.status === 401 || response.status === 403) {
    void _maybeNotifyUnauthorized(response)
  }
  return response
}

export { ServiceUnavailableError }

/**
 * Issue an HTTP call and parse the JSON response. Throws on non-2xx with the
 * extracted error message. Handles 204 (No Content) and empty bodies as `undefined`.
 */
export async function apiRequest<T>(path: string, opts?: ApiFetchOptions): Promise<T> {
  const response = await apiFetch(path, opts)

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(extractErrorMessage(errorData, response.statusText))
  }

  if (response.status === 204) {
    return undefined!
  }

  const contentType = response.headers.get('content-type')
  if (contentType && !contentType.includes('application/json')) {
    if (!getApiBase()) {
      throw new Error(
        'API endpoint not configured. Click the Settings icon (gear) in the header to set your API URL.',
      )
    }
    throw new Error(
      `API returned unexpected content type: ${contentType}. Check your API endpoint configuration in Settings.`,
    )
  }

  // Some endpoints (DELETE etc.) return 200 with an empty body — treat as undefined.
  const text = await response.text()
  return text ? parseJsonResponse<T>(text) : undefined!
}

/**
 * @deprecated Use {@link apiFetch} instead. Kept as a thin shim for legacy
 * callers (CanvasToolbox, useNodeActions, ImageHistoryPopover, useClipPreloader,
 * useClipThumbnails, scriptWriterClient). Delegating preserves their existing
 * call sites while inheriting credentials + promo + auth automatically.
 */
export async function authenticatedFetch(url: string, init?: RequestInit): Promise<Response> {
  return apiFetch(url, init)
}

export function getCachedAuthToken(): string | null {
  return cachedAuthToken
}

function getApiBase(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return getDefaultApiBase()
    }
    return localStorage.getItem(API_BASE_KEY) || getDefaultApiBase()
  }
  return getDefaultApiBase()
}

export function setApiBase(url: string): void {
  if (url) {
    localStorage.setItem(API_BASE_KEY, url)
  } else {
    localStorage.removeItem(API_BASE_KEY)
  }
}

export function getConfiguredApiBase(): string {
  return getApiBase()
}

/**
 * Build a project asset URL. If the path is already an absolute URL
 * (e.g. a signed Supabase URL from a published snapshot), returns it as-is.
 */
export function buildProjectAssetUrl(projectId: string, assetPath: string): string {
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  return `${getApiBase()}/projects/${projectId}/${assetPath}`
}

// Google API Key management (stored in browser)
export function getGoogleApiKey(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(GOOGLE_API_KEY)
  }
  return null
}

export function setGoogleApiKey(key: string): void {
  if (key) {
    localStorage.setItem(GOOGLE_API_KEY, key)
  } else {
    localStorage.removeItem(GOOGLE_API_KEY)
  }
}

export function hasGoogleApiKey(): boolean {
  return !!getGoogleApiKey()
}

// VEO3 API Keys management (for video generation)
export function getVeo3ApiKeys(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(VEO3_API_KEYS_KEY)
  }
  return null
}

export function setVeo3ApiKeys(keys: string): void {
  if (keys) {
    localStorage.setItem(VEO3_API_KEYS_KEY, keys)
  } else {
    localStorage.removeItem(VEO3_API_KEYS_KEY)
  }
}

export function hasVeo3ApiKeys(): boolean {
  return !!getVeo3ApiKeys()
}

// MusicGPT API Key management (for music generation)
export function getMusicGptApiKey(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(MUSICGPT_API_KEY_KEY)
  }
  return null
}

export function setMusicGptApiKey(key: string): void {
  if (key) {
    localStorage.setItem(MUSICGPT_API_KEY_KEY, key)
  } else {
    localStorage.removeItem(MUSICGPT_API_KEY_KEY)
  }
}

export function hasMusicGptApiKey(): boolean {
  return !!getMusicGptApiKey()
}

// Legacy Suno API compatibility functions (for backwards compatibility with SettingsPage)
export function getSunoApiKey(): string | null {
  return getMusicGptApiKey()
}

export function setSunoApiKey(key: string): void {
  setMusicGptApiKey(key)
}

export function hasSunoApiKey(): boolean {
  return hasMusicGptApiKey()
}

export function getSunoApiBase(): string | null {
  // Suno base URL is no longer used, return null
  return null
}

export function setSunoApiBase(base: string): void {
  // Suno base URL is no longer used, no-op
  void base
}

export function hasSunoApiBase(): boolean {
  // Suno base URL is no longer used
  return false
}

// Short-drama video model identifiers (wan_flf2v / kling are legacy, read-only)
export type VideoModel = 'veo' | 'wan_flf2v' | 'kling'

export interface VideoModelCapabilities {
  model: VideoModel
  label: string
  durations_seconds: number[]
  resolutions: Array<'480p' | '720p' | '1080p'>
  supports_interpolation: boolean
  interpolation_requires_duration_seconds?: number | null
  requires_end_frame: boolean
}

export interface VideoModelsResponse {
  models: VideoModelCapabilities[]
}

export interface PricingItem {
  model_id: string
  model_provider: string
  operation_type: string
  sparks_per_unit: number
  unit_type: string
  display_name: string
}

// Video Simulator Mode management (returns placeholder videos instead of calling real APIs)
export function getVideoSimulatorMode(): boolean {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(VIDEO_SIMULATOR_MODE_KEY) === 'true'
  }
  return false
}

export function setVideoSimulatorMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(VIDEO_SIMULATOR_MODE_KEY, 'true')
  } else {
    localStorage.removeItem(VIDEO_SIMULATOR_MODE_KEY)
  }
}


/**
 * Extract error message from API error response.
 * Handles different error formats:
 * 1. FastAPI's {detail: string}
 * 2. Our structured error {error: {message: string}}
 * 3. Dependency error {detail: {error: {...}, missing_dependencies: [...]}}
 */
function extractErrorMessage(errorData: ApiValue | null, fallback: string): string {
  if (!isApiRecord(errorData)) {
    return fallback
  }
  const data = errorData

  // Check for detail field
  const detailValue = data.detail
  if (typeof detailValue === 'string') {
    return detailValue
  }

  // Check for nested error in detail (dependency errors, etc.)
  if (isApiRecord(detailValue)) {
    const detail = detailValue
    // Dependency errors may come back as:
    // { detail: { detail: string, missing_dependencies: [...] } }
    const nestedDetail = stringField(detail, 'detail')
    if (nestedDetail) {
      const missingDeps = Array.isArray(detail.missing_dependencies) ? detail.missing_dependencies : null
      if (missingDeps && missingDeps.length > 0) {
        // Try to extract readable messages if present.
        const depMessages = missingDeps
          .map((dep) => {
            if (!isApiRecord(dep)) return null
            if (typeof dep.message === 'string') return dep.message
            if (typeof dep.type === 'string') return dep.type
            return null
          })
          .filter(Boolean)
          .slice(0, 5)
        if (depMessages.length > 0) {
          return `${nestedDetail}\n\nMissing:\n- ${depMessages.join('\n- ')}`
        }
      }
      return nestedDetail
    }
    if (isApiRecord(detail.error)) {
      return stringField(detail.error, 'message') ?? fallback
    }
  }

  // Check for top-level error object
  if (isApiRecord(data.error)) {
    return stringField(data.error, 'message') ?? fallback
  }

  return fallback
}

class ApiClient {
  private request<T>(path: string, options?: ApiFetchOptions): Promise<T> {
    return apiRequest<T>(path, options)
  }

  // API Keys (per-user, server-managed)
  async createApiKey(name: string): Promise<{ id: string; key: string; key_prefix: string; name: string }> {
    return this.request('/auth/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  // No raw key here, by design: the server stores only a hash, so the full
  // key exists exactly once — in createApiKey's response.
  async listApiKeys(): Promise<Array<{ id: string; key_prefix: string; name: string; created_at: string; last_used_at: string | null }>> {
    return this.request('/auth/api-keys')
  }

  async revokeApiKey(keyId: string): Promise<void> {
    return this.request(`/auth/api-keys/${keyId}`, { method: 'DELETE' })
  }

  // Admin — ad render manifests (dev page /dev/ads/renders)
  async listAdRenders(
    limit = 50,
  ): Promise<{
    renders: Array<{
      render_id: string
      recipe_id: string
      recipe_version: string
      product_name: string
      persona_id: string
      aspect: string
      duration_s: number
      cost_usd_total: number
      cost_sparks: number
      render_time_s: number
      validators_passing: boolean
      video_path: string
      notes: string
    }>
    total_found: number
    scanned_dirs: string[]
  }> {
    return this.request(`/admin/ads/renders?limit=${limit}`)
  }

  // Projects
  async listProjects(limit = 50, offset = 0): Promise<PaginatedResponse<ProjectListItem>> {
    return this.request(`/projects?limit=${limit}&offset=${offset}`)
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request(`/projects/${projectId}/full`)
  }

  async createProject(request: CreateProjectRequest): Promise<CreateProjectResponse> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async deleteProject(projectId: string): Promise<void> {
    await apiRequest<void>(`/projects/${projectId}`, { method: 'DELETE' })
  }

  // Actors (Track D1) — share the existing /actors routes with the canvas
  // console.  Backbone (ActorService + ActorStorage + user_actors table)
  // is the same; this client just exposes the HTTP surface.
  async listActors(style?: string, limit = 100, offset = 0): Promise<{
    actors: ActorRecord[]
    total: number
    style_filter: string | null
  }> {
    const params = new URLSearchParams()
    if (style) params.set('style', style)
    params.set('limit', String(limit))
    params.set('offset', String(offset))
    return this.request(`/actors?${params.toString()}`)
  }

  async getActor(actorId: string): Promise<ActorRecord> {
    return this.request(`/actors/${actorId}`)
  }

  async createActor(form: {
    name: string
    referenceImage: File
    age?: number
    visualTags?: string[]
  }): Promise<ActorRecord> {
    const fd = new FormData()
    fd.append('name', form.name)
    fd.append('reference_image', form.referenceImage)
    if (form.age !== undefined) fd.append('age', String(form.age))
    if (form.visualTags?.length) fd.append('visual_tags', form.visualTags.join(','))
    // request() defaults Content-Type to application/json which breaks
    // multipart; call apiRequest directly with no headers override so the
    // browser sets the correct multipart boundary.
    return apiRequest<ActorRecord>('/actors', { method: 'POST', body: fd })
  }

  async deleteActor(actorId: string): Promise<void> {
    await apiRequest<void>(`/actors/${actorId}`, { method: 'DELETE' })
  }

  async cloneProject(projectId: string, name?: string): Promise<{ id: string; name: string; message: string }> {
    return this.request(`/projects/${projectId}/clone`, {
      method: 'POST',
      body: JSON.stringify(name ? { name } : {}),
    })
  }

  async updateProject(
    projectId: string,
    data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
  ): Promise<ProjectListItem> {
    return this.request(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Read the project-level workflow config (style / aspect ratio / production target).
   * Backed by WorkflowState; surfaced under the canvas gear menu.
   */
  async getProjectConfig(projectId: string): Promise<ProjectConfig> {
    return this.request(`/projects/${projectId}/agentic/config`)
  }

  /**
   * Patch project-level workflow config. All fields optional;
   * each is independently applied via the orchestrator setters.
   * Existing canvas nodes are unaffected.
   */
  async updateProjectConfig(
    projectId: string,
    data: UpdateProjectConfigRequest,
  ): Promise<ProjectConfig> {
    return this.request(`/projects/${projectId}/agentic/config`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Assets
  getAssetUrl(projectId: string, assetPath: string, options?: { width?: number; bustCache?: string }): string {
    // Full URLs (e.g. Supabase public URLs from upload nodes) are used as-is
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }

    const baseUrl = getApiBase()
    // Asset paths may already include 'assets/' prefix
    let url: string
    if (assetPath.startsWith('assets/')) {
      url = `${baseUrl}/projects/${projectId}/${assetPath}`
    } else {
      url = `${baseUrl}/projects/${projectId}/assets/${assetPath}`
    }
    // Build query params
    const params = new URLSearchParams()
    if (options?.width) {
      params.set('w', String(options.width))
    }
    if (options?.bustCache) {
      params.set('v', options.bustCache)
    }
    // Include auth token for <img> tags (can't use Authorization header)
    const token = getCachedAuthToken()
    if (token) {
      params.set('token', token)
    }
    // Include publication token for unauthenticated access to published project assets
    if (currentPublicationToken) {
      params.set('publication_token', currentPublicationToken)
    }
    const queryString = params.toString()
    if (queryString) {
      url += `?${queryString}`
    }
    return url
  }

  // Convenience method for thumbnails
  getThumbnailUrl(projectId: string, assetPath: string, width: number = 200, bustCache?: string): string {
    return this.getAssetUrl(projectId, assetPath, { width, bustCache })
  }

  // Get a library asset URL for thumbnails of library-sourced nodes
  getLibraryAssetUrl(assetPath: string): string {
    if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
      return assetPath
    }
    const baseUrl = getApiBase()
    const clean = assetPath.startsWith('assets/') ? assetPath.slice(7) : assetPath
    let url = `${baseUrl}/library/assets/${clean}`
    const token = getCachedAuthToken()
    if (token) {
      url += `?token=${encodeURIComponent(token)}`
    }
    return url
  }

  // Upload an asset file (image, video, audio) through the backend API
  async uploadAsset(
    projectId: string,
    file: File,
    name?: string
  ): Promise<{ path: string; filename: string; size: number }> {
    const formData = new FormData()
    formData.append('file', file)
    if (name) formData.append('name', name)
    return apiRequest(`/projects/${projectId}/assets/upload`, {
      method: 'POST',
      body: formData,
    })
  }

  // Sparks (credits)
  async getSparks(): Promise<{ balance: number }> {
    return this.request('/sparks')
  }

  /**
   * @deprecated The server now returns 403 with code
   * REFUND_PUBLIC_API_CLOSED. Credit top-ups go through
   * {@link createCheckoutSession} → Stripe Checkout → webhook.
   * Kept as a stub so older builds throw a clear error instead of
   * silently 4xx-ing.
   */
  async addSparks(_sparks: number, _idempotencyKey: string): Promise<never> {
    throw new Error(
      'addSparks is closed — use createCheckoutSession(packId) to top up via Stripe',
    )
  }

  /**
   * Create a Stripe Checkout Session for a credit pack. Returns the
   * Stripe-hosted `session_url` the caller should redirect the user to.
   * The actual credit grant happens server-side on
   * `checkout.session.completed` webhook delivery — NOT on the
   * client's success_url callback (which can be spoofed).
   *
   * Valid pack ids come from `stripe_service.PACK_TO_SPARKS` on the
   * backend (credits_500 / credits_2000 / credits_5000 / credits_11000).
   * Returns 503 when Stripe isn't configured (local dev).
   */
  async createCheckoutSession(packId: string): Promise<{
    session_id: string
    session_url: string
    pack_id: string
    sparks: number
  }> {
    return this.request('/sparks/checkout/create-session', {
      method: 'POST',
      body: JSON.stringify({ pack_id: packId }),
    })
  }

  /**
   * Recurring subscription Checkout Session (Stripe `mode='subscription'`).
   * Returns the hosted `session_url` for redirect. Per-renewal credit
   * grants happen on `invoice.paid` webhook delivery server-side.
   *
   * `planTier` is one of `'hobby' | 'pro' | 'studio'` from the backend's
   * `stripe_service.PLAN_REGISTRY`. Returns 503 when `SUBSCRIPTIONS_ENABLED`
   * is off on the deploy.
   */
  async createSubscriptionCheckoutSession(planTier: string): Promise<{
    session_id: string
    session_url: string
    plan_tier: string
    monthly_credits: number
  }> {
    return this.request('/sparks/subscriptions/checkout/create-session', {
      method: 'POST',
      body: JSON.stringify({ plan_tier: planTier }),
    })
  }

  /**
   * Open the Stripe Customer Portal for the current user (plan switch,
   * cancel, payment method, invoice history). Returns the portal URL
   * for redirect.
   *
   * 400 when the user has never been through Stripe Checkout — the
   * caller should send them through subscription Checkout first.
   * 503 when `SUBSCRIPTIONS_ENABLED` is off.
   */
  async createPortalSession(): Promise<{ portal_url: string }> {
    return this.request('/sparks/subscriptions/portal/create-session', {
      method: 'POST',
    })
  }

  /**
   * Read the current user's subscription, or `null` when not subscribed.
   * Stripe identifiers (sub_id / customer_id) are intentionally NOT
   * exposed to the frontend.
   */
  async getSubscription(): Promise<{
    plan_tier: string
    status: string
    current_period_end: string | null
    cancel_at_period_end: boolean
    monthly_credits: number
  } | null> {
    return this.request('/sparks/subscriptions/me')
  }

  async getPricing(): Promise<{ pricing: PricingItem[] }> {
    return this.request('/pricing')
  }

  // Debug Feedback API
  async submitWorkspaceFeedback(
    projectId: string,
    request: DebugFeedbackRequest
  ): Promise<DebugFeedbackResponse> {
    return this.request(`/projects/${projectId}/workspace/feedback`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // GitHub Feedback API (creates GitHub Issues from user feedback)
  async submitGithubFeedback(
    request: GithubFeedbackRequest
  ): Promise<GithubFeedbackResponse> {
    return this.request('/api/feedback/github-issue', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  /**
   * Cancel the running agent execution for a project.
   * RFC 0061: Stop button for canvas execution.
   */
  async cancelAgentExecution(projectId: string): Promise<{ cancelled: boolean; reset_nodes?: number; reason?: string }> {
    return this.request(`/projects/${projectId}/agentic/cancel-node`, {
      method: 'POST',
    })
  }

  async getCostSummary(projectId: string): Promise<CostSummaryResponse> {
    return this.request(`/projects/${projectId}/agentic/cost-summary`)
  }

  // Dashboard Tabs
  async listTabs(): Promise<{ tabs: DashboardTab[] }> {
    return this.request('/dashboard/tabs')
  }

  async createTab(name: string): Promise<DashboardTab> {
    return this.request('/dashboard/tabs', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  }

  async updateTab(
    tabId: string,
    data: { name?: string; sort_by?: TabSortBy; sort_direction?: TabSortDirection }
  ): Promise<DashboardTab> {
    return this.request(`/dashboard/tabs/${tabId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteTab(tabId: string): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}`, { method: 'DELETE' })
  }

  async reorderTabs(tabIds: string[]): Promise<{ tabs: DashboardTab[] }> {
    return this.request('/dashboard/tabs/reorder', {
      method: 'PUT',
      body: JSON.stringify({ tab_ids: tabIds }),
    })
  }

  async assignProjectToTab(tabId: string, projectId: string): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}/projects/${projectId}`, {
      method: 'POST',
    })
  }

  async unassignProjectFromTab(tabId: string, projectId: string): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}/projects/${projectId}`, {
      method: 'DELETE',
    })
  }

  async reorderProjects(tabId: string, projectIds: string[]): Promise<void> {
    return this.request(`/dashboard/tabs/${tabId}/projects/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ project_ids: projectIds }),
    })
  }
}

export interface NodeUsageItem {
  node_id: string
  node_type: string
  input_tokens: number
  output_tokens: number
  llm_model: string
  token_cost_usd: number
  images_generated: number
  videos_generated: number
  video_seconds: number
  estimated_sparks: number
  charged_sparks: number
  duration_ms: number | null
  status: 'COMPLETED' | 'FAILED'
}

export interface CostSummaryResponse {
  total_sparks_charged: number
  total_sparks_estimated: number
  total_input_tokens: number
  total_output_tokens: number
  total_token_cost_usd: number
  total_images: number
  total_videos: number
  node_count: number
  nodes: NodeUsageItem[]
}

// Debug Feedback API types
export interface DebugFeedbackRequest {
  feedback: string
  category?: 'bug' | 'feature' | 'question' | 'other'
  browser_info?: {
    user_agent?: string
    url?: string
  }
}

export interface DebugFeedbackResponse {
  id: string
  timestamp: string
}

// GitHub Feedback API types
export interface GithubFeedbackRequest {
  title: string
  description: string
  category: 'bug' | 'feature_request' | 'question'
  project_id?: string
  browser_info?: string
  page_url?: string
}

export interface GithubFeedbackResponse {
  issue_number: number
  html_url: string
}

export const api = new ApiClient()

// Workspace Chat API (SSE streaming)
export interface WorkspaceChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface WorkspaceChatOptions {
  projectId: string
  workspaceId: string
  canvasId?: string  // Which canvas to operate on (default: 'project')
  messages: WorkspaceChatMessage[]
  onChunk: (text: string) => void
  onComplete: () => void
  onError: (error: Error) => void
}

// =============================================================================
// Voice listing
// =============================================================================

export interface VoiceSummary {
  provider: string
  provider_label: string
  voice_id: string
  name: string
  description: string
  preview_url: string | null
  labels: Record<string, string>
}

export interface VoiceProviderSummary {
  id: string
  label: string
}

const cachedVoices = new Map<string, VoiceSummary[]>()
let cachedVoiceProviders: VoiceProviderSummary[] | null = null

export async function listVoices(provider?: string): Promise<VoiceSummary[]> {
  const normalizedProvider = provider?.trim().toLowerCase() ?? ''
  const cacheKey = normalizedProvider || '__all__'
  const cached = cachedVoices.get(cacheKey)
  if (cached) return cached

  const voices = await apiRequest<VoiceSummary[]>(
    normalizedProvider ? `/voices?provider=${encodeURIComponent(normalizedProvider)}` : '/voices',
  )
  cachedVoices.set(cacheKey, voices)
  return voices
}

export async function listVoiceProviders(): Promise<VoiceProviderSummary[]> {
  if (cachedVoiceProviders) return cachedVoiceProviders
  const providers = await apiRequest<VoiceProviderSummary[]>('/voices/providers')
  cachedVoiceProviders = providers
  return providers
}

/**
 * Send a chat message and stream the response via SSE.
 * Returns an AbortController to cancel the stream.
 */
export async function sendWorkspaceChat(options: WorkspaceChatOptions): Promise<AbortController> {
  const { projectId, workspaceId, canvasId = 'project', messages, onChunk, onComplete, onError } = options
  const controller = new AbortController()

  try {
    const response = await apiFetch(
      `/projects/${projectId}/workspaces/${workspaceId}/messages`,
      {
        method: 'POST',
        includeApiKey: true,
        body: JSON.stringify({ messages, canvas_id: canvasId, stream: true }),
        signal: controller.signal,
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      // Handle different error formats from FastAPI
      let errorMessage = response.statusText
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail
        } else if (Array.isArray(errorData.detail)) {
          // FastAPI validation errors come as array of {msg, loc, type}
          errorMessage = errorData.detail
            .map((e: { msg?: string; message?: string }) => e.msg || e.message || JSON.stringify(e))
            .join('; ')
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail)
        }
      }
      throw new Error(errorMessage)
    }
    
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }
    
    const decoder = new TextDecoder()
    let buffer = ''
    
    const processStream = async () => {
      try {
        // Helper to process buffered lines
        const processBuffer = () => {
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                onComplete()
                return true // Signal completion
              }
              if (data.startsWith('[ERROR]')) {
                onError(new Error(data.slice(8)))
                return true // Signal completion (with error)
              }
              onChunk(data)
            }
          }
          return false // Not yet complete
        }

        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done
          
          if (result.value) {
            buffer += decoder.decode(result.value, { stream: true })
            if (processBuffer()) return
          }
          
          if (done) {
            // Flush any remaining data in decoder
            buffer += decoder.decode()
            // Process any remaining buffer content
            if (buffer.trim()) {
              // Add newline to ensure the last line is processed
              buffer += '\n'
              if (processBuffer()) return
            }
          }
        }
        onComplete()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Stream was cancelled
          return
        }
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    }
    
    processStream()
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)))
  }
  
  return controller
}

/**
 * Options for executing a workspace tool directly (without LLM).
 * Used by Continue buttons for deterministic workflow steps.
 */
export interface ExecuteToolOptions {
  projectId: string
  workspaceId: string
  canvasId?: string
  toolName: string
  toolArgs?: ApiRecord
  onChunk: (chunk: string) => void
  onComplete: () => void
  onError: (error: Error) => void
}

/**
 * Execute a workspace tool directly without LLM involvement.
 * This is the unified path for Continue button workflow steps.
 * Returns an AbortController to cancel the stream.
 */
export async function executeWorkspaceTool(options: ExecuteToolOptions): Promise<AbortController> {
  const { projectId, workspaceId, canvasId = 'project', toolName, toolArgs = {}, onChunk, onComplete, onError } = options
  const controller = new AbortController()

  try {
    const response = await apiFetch(
      `/projects/${projectId}/workspaces/${workspaceId}/execute-tool`,
      {
        method: 'POST',
        includeApiKey: true,
        body: JSON.stringify({ tool_name: toolName, tool_args: toolArgs, canvas_id: canvasId }),
        signal: controller.signal,
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      let errorMessage = response.statusText
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail
            .map((e: { msg?: string; message?: string }) => e.msg || e.message || JSON.stringify(e))
            .join('; ')
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail)
        }
      }
      throw new Error(errorMessage)
    }
    
    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }
    
    const decoder = new TextDecoder()
    let buffer = ''
    
    const processStream = async () => {
      try {
        const processBuffer = () => {
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                onComplete()
                return true
              }
              if (data.startsWith('[ERROR]')) {
                onError(new Error(data.slice(8)))
                return true
              }
              onChunk(data)
            }
          }
          return false
        }

        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done
          
          if (result.value) {
            buffer += decoder.decode(result.value, { stream: true })
            if (processBuffer()) return
          }
          
          if (done) {
            buffer += decoder.decode()
            if (buffer.trim()) {
              buffer += '\n'
              if (processBuffer()) return
            }
          }
        }
        onComplete()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        onError(err instanceof Error ? err : new Error(String(err)))
      }
    }
    
    processStream()
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)))
  }
  
  return controller
}

// ============================================================================
// Conversation API
// ============================================================================

export interface WorkflowState {
  stage: string  // 'idle', 'expand_story', 'generate_characters', etc.
  active_job_id?: string | null  // Job currently running
  pending_action?: string | null  // 'continue' | 'refine'
}

export interface Conversation {
  id: string
  project_id: string
  title: string
  active_canvas_id: string  // Which canvas this conversation is working on
  workflow?: WorkflowState | null  // Workflow progress tracking
  created_at: string
  updated_at: string
}

export interface ConversationWithMessages extends Conversation {
  messages: WorkspaceChatMessage[]
  // Note: canvas_state is no longer part of conversation - canvases are independent
}

export interface CanvasState {
  nodes: object[]
  edges: object[]
  viewport?: { x: number; y: number; zoom: number } | null
  tainted_nodes?: string[]
  updated_at?: string
}

export interface YDocNodePresence {
  present_node_ids: string[]
  missing_node_ids: string[]
}

// Conversation API helper — kept as a name so the public surface (listConversations etc.)
// reads cleanly. Delegates to apiRequest, which already handles empty bodies.
function conversationRequest<T>(path: string, options?: ApiFetchOptions): Promise<T> {
  return apiRequest<T>(path, options)
}

/**
 * List all conversations for a project
 */
export async function listConversations(projectId: string): Promise<Conversation[]> {
  const response = await conversationRequest<{ conversations: Conversation[] }>(
    `/projects/${projectId}/workspaces`
  )
  return response.conversations || []
}

/**
 * Create a new conversation
 */
export async function createConversation(
  projectId: string,
  options?: { title?: string; canvas_id?: string }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  })
}

/**
 * Get or create the agent conversation (RFC 0036)
 */
export async function getOrCreateAgentConversation(
  projectId: string
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/agent`, {
    method: 'POST',
  })
}

/**
 * Update a conversation (e.g., rename or switch canvas)
 */
export async function updateConversation(
  projectId: string,
  conversationId: string,
  options: { title?: string; canvas_id?: string; workflow?: WorkflowState }
): Promise<Conversation> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify(options),
  })
}

/**
 * Get a conversation with messages
 */
export async function getConversation(
  projectId: string,
  conversationId: string
): Promise<ConversationWithMessages> {
  return conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`)
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  projectId: string,
  conversationId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/${conversationId}`, {
    method: 'DELETE',
  })
}

/**
 * List all canvases for a project
 */
export async function listCanvases(
  projectId: string
): Promise<{ canvases: Array<{ id: string; updated_at: string; node_count: number }> }> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases`)
}

/**
 * Get canvas state by ID (canvases are independent from conversations)
 */
export async function getCanvasState(
  projectId: string,
  canvasId: string = 'project'
): Promise<CanvasState> {
  return conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`)
}

/**
 * Read live backend YDoc node presence after frontend sync writes.
 */
export async function getYDocNodePresence(
  projectId: string,
  canvasId: string,
  nodeIds: string[],
): Promise<YDocNodePresence> {
  const params = new URLSearchParams()
  for (const nodeId of nodeIds) {
    params.append('node_ids', nodeId)
  }
  const query = params.toString()
  return conversationRequest(
    `/projects/${projectId}/workspaces/canvases/${canvasId}/ydoc-node-presence${query ? `?${query}` : ''}`,
  )
}

/**
 * Delete a canvas (cannot delete 'project' canvas)
 */
export async function deleteCanvas(
  projectId: string,
  canvasId: string
): Promise<void> {
  await conversationRequest(`/projects/${projectId}/workspaces/canvases/${canvasId}`, {
    method: 'DELETE',
  })
}


// =============================================================================
// DAG Proposal API (RFC 0079: Manual Canvas Node Persistence)
// =============================================================================

/**
 * Execute a node through the Orchestrator (RFC-0098).
 *
 * Fire-and-forget: progress and completion arrive via the existing
 * /agent/connect SSE stream (node status changes propagate through YDoc).
 *
 * Node data (in_/out_ fields) must be written to YDoc before calling.
 * This endpoint only accepts commands: which node, what action, how many times.
 *
 * @param projectId - Project ID
 * @param nodeId - Node ID to execute
 * @param action - Action to perform (default "generate")
 * @param count - Number of sequential generations to run (1-6, default 1)
 */
export async function executeNode(
  projectId: string,
  nodeId: string,
  action?: string,
  count?: number,
  inputUpdates?: ApiRecord,
): Promise<{ status: string; node_id: string }> {
  const body: ApiRecord = { node_id: nodeId, action: action || 'generate' }
  if (count !== undefined && count > 1) {
    body.count = count
  }
  if (inputUpdates && Object.keys(inputUpdates).length > 0) {
    body.input_updates = inputUpdates
  }

  const response = await apiFetch(
    `/projects/${projectId}/agentic/execute-node`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    // Backend returns 402 with structured detail when the User-path
    // sparks-balance gate trips. Surface as a typed error so callers
    // can render the InsufficientSparksModal instead of a toast.
    if (response.status === 402 && _isInsufficientSparksDetail(errorData?.detail)) {
      throw new InsufficientSparksError(
        errorData.detail.balance,
        errorData.detail.required,
        errorData.detail.message ?? 'Insufficient credits',
      )
    }
    throw new Error(
      errorData?.detail || `Failed to execute node: ${response.statusText}`,
    )
  }

  return response.json()
}

/**
 * Cancel a running node execution and refund sparks.
 */
export async function cancelNodeExecution(
  projectId: string,
  nodeId: string
): Promise<{ ok: boolean; node_id: string }> {
  const response = await apiFetch(
    `/projects/${projectId}/agentic/cancel-node/${nodeId}`,
    { method: 'POST' },
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(
      errorData?.detail || `Failed to cancel node: ${response.statusText}`,
    )
  }

  return response.json()
}

/**
 * Thrown by `executeNode` when the backend rejects with HTTP 402
 * `{ code: 'insufficient_sparks', balance, required, message }`. Catch
 * it in the workspace layer and show <InsufficientSparksModal>.
 */
export class InsufficientSparksError extends Error {
  readonly code = 'insufficient_sparks'
  constructor(
    public readonly balance: number,
    public readonly required: number,
    message: string,
  ) {
    super(message)
    this.name = 'InsufficientSparksError'
  }
}

function _isInsufficientSparksDetail(
  d: ApiValue | null,
): d is { code: 'insufficient_sparks'; balance: number; required: number; message?: string } {
  if (!isApiRecord(d)) {
    return false
  }
  return (
    d.code === 'insufficient_sparks' &&
    typeof d.balance === 'number' &&
    typeof d.required === 'number'
  )
}

// ---------------------------------------------------------------------------
// Publication API (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export interface PublishResponse {
  share_token: string
  share_url: string
  version_number: number
  created_at: string
}

export interface PublicationVersionInfo {
  version_number: number
  version_slug: string
  share_url: string
  created_at: string
}

export interface PublicationStatus {
  is_published: boolean
  share_token?: string
  share_url?: string
  version_number?: number
  updated_at?: string
  versions: PublicationVersionInfo[]
}

export interface PublishedQuestionOption {
  value?: string
  label?: string
  description?: string
}

export interface PublishedQuestion {
  answer?: string | null
  tool_use_id?: string
  question?: string
  options?: PublishedQuestionOption[]
}

export type PublishedNodeData = Record<string, ApiValue>

export interface PublishedChatMessage {
  role: 'user' | 'assistant'
  content: string
  agent?: string
  timestamp?: string
  referenced_node_ids?: string[]
  questions?: PublishedQuestion[]
}

export interface PublishedSnapshot {
  project_name: string
  project_id: string
  snapshot: {
    nodes: Array<{
      id: string
      type: string
      position: { x: number; y: number }
      data: PublishedNodeData
      parentNode?: string
      extent?: 'parent'
      width?: number
      height?: number
    }>
    edges: Array<{ id: string; source: string; target: string; type?: string; sourceHandle?: string; targetHandle?: string }>
    viewport?: { x: number; y: number; zoom: number }
  }
  messages: PublishedChatMessage[]
  published_at: string
}

export interface VersionListItem {
  version_number: number
  created_at: string
  project_name: string
}

export interface VersionDetail {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  published_at: string
  version_number: number
}

export interface LiveSnapshot {
  project_name: string
  snapshot: PublishedSnapshot['snapshot']
  is_live: boolean
}

export interface VersionShareLink {
  share_token: string
  share_url: string
  version_number: number
}

export async function publishProject(projectId: string): Promise<PublishResponse> {
  return apiRequest(`/projects/${projectId}/publication`, { method: 'POST' })
}

export async function unpublishProject(projectId: string): Promise<void> {
  await apiRequest<void>(`/projects/${projectId}/publication`, { method: 'DELETE' })
}

export async function getPublicationStatus(projectId: string): Promise<PublicationStatus> {
  return apiRequest(`/projects/${projectId}/publication`)
}

export async function getPublishedSnapshot(projectToken: string, versionSlug: string): Promise<PublishedSnapshot> {
  // Public endpoint — no auth needed (skipAuth still sends cookies + promo if any).
  return apiRequest(`/publications/${projectToken}/${versionSlug}`, { skipAuth: true })
}

export async function getPublicationVersions(projectId: string): Promise<VersionListItem[]> {
  return apiRequest(`/projects/${projectId}/publication/versions`)
}

export async function getPublicationVersion(projectId: string, versionNumber: number): Promise<VersionDetail> {
  return apiRequest(`/projects/${projectId}/publication/versions/${versionNumber}`)
}

export async function getPublicationLive(projectId: string): Promise<LiveSnapshot> {
  return apiRequest(`/projects/${projectId}/publication/live`)
}

export async function createVersionShareLink(projectId: string, versionNumber: number): Promise<VersionShareLink> {
  return apiRequest(`/projects/${projectId}/publication/versions/${versionNumber}/share`, { method: 'POST' })
}

export async function clonePublishedProject(
  projectToken: string,
  versionSlug: string,
): Promise<{ id: string; name: string; message: string }> {
  return apiRequest(`/publications/${projectToken}/${versionSlug}/clone`, { method: 'POST' })
}
